import React, {useEffect} from 'react';
import {Linking, TouchableOpacity, View} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCAccordion from '@/Atoms/CCAccordion';
import Spacer from '@/Atoms/Spacer';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {FontSizes, vs} from '@/Utils/Dimensions';
import useLogChatWithUsClick, {
  FaqAction,
} from '@/Hooks/GetSupportModalApi/useLogChatWithUsClick';
import {buildSupportWhatsappLink} from './supportWhatsapp';
import {formatReplyTime} from './supportEta';

export interface FaqItem {
  question?: string | null;
  help_text: string;
  deflectable: boolean;
  link_url?: string | null;
  link_label?: string | null;
}

interface FaqStepProps {
  category: string;
  subCategory: string;
  // CategoriesAndSLAMapping id of the picked sub-category; sent with the
  // "Chat with us" tap log so the WhatsApp chat can be attributed to it.
  subCategoryId: string | null;
  faqs: FaqItem[];
  onBack: () => void;
  onDismiss: () => void;
  // link_url values that aren't http(s) are treated as an in-app screen
  // name (e.g. "Renewal") and routed here instead of Linking.openURL --
  // lets a FAQ row nudge straight into a real in-app flow, not just an
  // external link.
  onNavigate?: (screen: string) => void;
  // Opens the ticket form for this sub-category ("Still raise an issue"), for
  // customers who have no WhatsApp or don't want to chat. The link only
  // shows when this is passed.
  onRaiseIssue?: () => void;
  // Reply times shown under the buttons, in minutes, from the categories API:
  // "Chat with us" vs this sub-category's ticket SLA. Missing = no line.
  chatReplyMinutes?: number | null;
  ticketReplyMinutes?: number | null;
}

interface ReplyTimes {
  chat: string | null;
  ticket: string | null;
  // Chat really is faster, so the lines say "Fastest" / "Slower".
  chatFaster: boolean;
}

// Each FAQ item is fully self-contained: its own content, its own link (if
// any), and its own action row. "Got it" only appears when this specific
// item can be self-resolved (deflectable), since nudging someone toward a
// dismiss button on a FAQ that explicitly needs a human doesn't make sense.
// "Chat with us" is the main way to a human; "Still raise an issue" sits
// below it as the quieter, slower option, each with its reply time.
const FaqItemBody: React.FC<{
  item: FaqItem;
  onGotIt: (question: string | null) => void;
  onChat: (question: string | null) => void;
  onRaiseIssue?: (question: string | null) => void;
  replyTimes: ReplyTimes;
  onNavigate?: (screen: string) => void;
}> = ({item, onGotIt, onChat, onRaiseIssue, replyTimes, onNavigate}) => (
  <View>
    <CCText fontSize={FontSizes[14]} style={{lineHeight: 20}}>
      {item.help_text}
    </CCText>
    {item.link_url ? (
      <>
        <Spacer spacing={vs(8)} />
        <TouchableOpacity
          onPress={() =>
            /^https?:\/\//.test(item.link_url!)
              ? Linking.openURL(item.link_url!)
              : onNavigate?.(item.link_url!)
          }>
          <CCText
            fontSize={FontSizes[13]}
            color={CCColors?.Green}
            style={{textDecorationLine: 'underline'}}>
            {item.link_label || 'View full policy'}
          </CCText>
        </TouchableOpacity>
      </>
    ) : null}
    <Spacer spacing={vs(14)} />
    {/* CCButton puts `style` on an inner view, so the buttons only share the
        row through `parentStyle`. Chat's reply time sits in the same column
        as the button, so it stays centred under it at any width. */}
    <View style={{flexDirection: 'row', alignItems: 'flex-start', gap: 10}}>
      {item.deflectable ? (
        <CCButton
          parentStyle={{flex: 1}}
          style={{
            backgroundColor: CCColors?.Buttons?.Background,
            borderColor: CCColors?.Buttons?.Background,
          }}
          titleStyle={{color: CCColors?.TextColor?.Primary}}
          onPress={() => onGotIt(item.question || null)}>
          Got it
        </CCButton>
      ) : null}
      <View style={{flex: 1}}>
        <CCButton
          style={{backgroundColor: '#25D366', borderColor: '#25D366'}}
          onPress={() => onChat(item.question || null)}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            <Ionicons name="logo-whatsapp" size={15} color="#fff" />
            <CCText fontWeight="bold" fontSize={FontSizes[14]} color="#fff">
              Chat with us
            </CCText>
          </View>
        </CCButton>
        {replyTimes?.chat ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 3,
              marginTop: 6,
            }}>
            {replyTimes?.chatFaster ? (
              <Ionicons name="flash" size={12} color={CCColors?.Green} />
            ) : null}
            <CCText fontSize={FontSizes[12]} color={CCColors?.Green}>
              {replyTimes?.chatFaster
                ? `Fastest · replies in ~${replyTimes?.chat}`
                : `Replies in ~${replyTimes?.chat}`}
            </CCText>
          </View>
        ) : null}
      </View>
    </View>
    {/* Deliberately quiet, like "Message the founder" on the category step:
        chat is the route we want people to take, this is the fallback. */}
    {onRaiseIssue ? (
      <TouchableOpacity
        testID="faq-still-raise-issue"
        onPress={() => onRaiseIssue(item.question || null)}
        style={{alignSelf: 'flex-end', marginTop: vs(14)}}>
        <View
          style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 4,
          }}>
          <Ionicons
            name="create-outline"
            size={14}
            color={CCColors?.TextColor?.Grey}
          />
          <CCText
            fontSize={FontSizes[12]}
            color={CCColors?.TextColor?.Grey}
            style={{textDecorationLine: 'underline'}}>
            Still raise an issue
          </CCText>
          {replyTimes?.ticket ? (
            <CCText fontSize={FontSizes[12]} color={CCColors?.TextColor?.Grey}>
              {`· reply within ${replyTimes?.ticket}`}
            </CCText>
          ) : null}
        </View>
      </TouchableOpacity>
    ) : null}
  </View>
);

const FaqStep: React.FC<FaqStepProps> = ({
  category,
  subCategory,
  subCategoryId,
  faqs,
  onBack,
  onDismiss,
  onNavigate,
  onRaiseIssue,
  chatReplyMinutes,
  ticketReplyMinutes,
}) => {
  const {mutate: logFaqAction} = useLogChatWithUsClick();
  // Every log is fire-and-forget and best-effort: a failure must never block
  // the customer, and opening WhatsApp after an awaited request trips popup
  // blockers on the web.
  const log = (action: FaqAction, question: string | null) => {
    if (subCategoryId) {
      logFaqAction({
        sub_category_id: subCategoryId,
        faq_question: question,
        action,
      });
    }
  };
  // One view per time the FAQ step is shown for a sub-category.
  useEffect(() => {
    log('faq_viewed', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategoryId]);
  const openChat = (question: string | null) => {
    log('chat_with_us', question);
    Linking.openURL(buildSupportWhatsappLink(category, subCategory));
  };
  const gotIt = (question: string | null) => {
    log('got_it', question);
    onDismiss();
  };
  const raiseIssue = (question: string | null) => {
    log('raise_issue', question);
    onRaiseIssue?.();
  };
  const replyTimes: ReplyTimes = {
    chat: formatReplyTime(chatReplyMinutes),
    ticket: formatReplyTime(ticketReplyMinutes),
    chatFaster:
      !!chatReplyMinutes &&
      !!ticketReplyMinutes &&
      chatReplyMinutes < ticketReplyMinutes,
  };
  const isAccordion = faqs.length > 1 || faqs.some(f => !!f.question);
  // faqs should never be empty here -- index.tsx only reaches this step when
  // helpTextMap[subCategoryId] is non-empty -- but guard anyway rather than
  // relying on the caller: faqs[0] on an empty array is undefined, not null,
  // and single.deflectable below would throw.
  const single = !isAccordion && faqs.length > 0 ? faqs[0] : null;

  return (
    <View style={{paddingHorizontal: 4}}>
      <TouchableOpacity
        onPress={onBack}
        style={{flexDirection: 'row', alignItems: 'center', gap: 4}}>
        <Ionicons
          name="chevron-back"
          size={16}
          color={CCColors?.TextColor?.Grey}
        />
        <CCText fontSize={FontSizes[13]} color={CCColors?.TextColor?.Grey}>
          Change category
        </CCText>
      </TouchableOpacity>
      <Spacer spacing={vs(16)} />
      <CCText fontSize={FontSizes[12]} color={CCColors?.TextColor?.Grey}>
        {category} → {subCategory}
      </CCText>
      <Spacer spacing={vs(10)} />

      {single ? (
        <View
          style={{
            backgroundColor: updateColorWithOpacity(CCColors?.Green, 0.07),
            borderLeftWidth: 3,
            borderLeftColor: CCColors?.Green,
            borderRadius: 10,
            padding: 16,
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
            <Ionicons
              name={single.deflectable ? 'bulb-outline' : 'person-outline'}
              size={20}
              color={CCColors?.Green}
            />
            <CCText fontWeight="bold" fontSize={FontSizes[15]}>
              Before you continue
            </CCText>
          </View>
          <Spacer spacing={vs(10)} />
          <FaqItemBody
            item={single}
            onGotIt={gotIt}
            onChat={openChat}
            onRaiseIssue={onRaiseIssue ? raiseIssue : undefined}
            replyTimes={replyTimes}
            onNavigate={onNavigate}
          />
        </View>
      ) : faqs.length > 0 ? (
        <CCAccordion
          paddingVertical={0}
          sections={faqs.map(f => ({
            title: f.question || f.help_text,
            renderContent: () => (
              <FaqItemBody
                item={f}
                onGotIt={gotIt}
                onChat={openChat}
                onRaiseIssue={onRaiseIssue ? raiseIssue : undefined}
                replyTimes={replyTimes}
                onNavigate={onNavigate}
              />
            ),
          }))}
        />
      ) : null}
    </View>
  );
};

export default FaqStep;
